/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* MishUUU (8 Oct 2026): the popup opens after 60 seconds of time on site per visit.
   The clock only runs once each page has fully loaded and while the tab is visible,
   and it carries over between pages (sessionStorage). The theme editor's
   "Delay seconds" setting is no longer used for this popup. */
const MU_POPUP_DELAY_MS = 60000;
const MU_POPUP_ELAPSED_KEY = 'mu-popup-elapsed-ms';

/* global Modal */

if (!customElements.get('pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class PopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.querySelector('.alert')) {
            this.open();
          } else if (this.dataset.trigger === 'delay') {
            this.startVisitTimer(MU_POPUP_DELAY_MS);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          } else if (this.dataset.trigger === 'clipboard') {
            this.copyHandler = this.copyHandler || this.handleCopy.bind(this);
            document.addEventListener('copy', this.copyHandler);
          }
        }
      }

      /**
       * Opens the popup after totalMs of visible time on site this visit,
       * counted only after the page has fully loaded.
       * @param {number} totalMs - Time on site needed before opening.
       */
      startVisitTimer(totalMs) {
        let elapsed = 0;
        try { elapsed = Number(sessionStorage.getItem(MU_POPUP_ELAPSED_KEY)) || 0; } catch (e) { /* storage blocked */ }

        let loaded = false;
        let done = false;
        let startedAt = null;
        let timer = null;

        const save = () => {
          if (startedAt !== null) {
            elapsed += Date.now() - startedAt;
            startedAt = null;
          }
          try { sessionStorage.setItem(MU_POPUP_ELAPSED_KEY, String(elapsed)); } catch (e) { /* storage blocked */ }
        };

        const pause = () => {
          clearTimeout(timer);
          save();
        };

        const run = () => {
          if (!loaded || done || document.visibilityState !== 'visible') return;
          clearTimeout(timer);
          if (startedAt === null) startedAt = Date.now();
          timer = setTimeout(() => {
            done = true;
            save();
            if (!getCookie(this.cookie)) this.open();
          }, Math.max(0, totalMs - elapsed));
        };

        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') run(); else pause();
        });
        window.addEventListener('pagehide', pause);

        const onLoaded = () => { loaded = true; run(); };
        if (document.readyState === 'complete') onLoaded();
        else window.addEventListener('load', onLoaded, { once: true });
      }

      /**
       * Triggers when the web component is removed from the DOM
       */
      disconnectedCallback() {
        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }

        if (this.copyHandler) {
          document.removeEventListener('copy', this.copyHandler);
        }
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'copy' events on the body.
       */
      handleCopy() {
        if (!getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'close' events on the modal.
       */
      close() {
        super.close();
        setCookie(this.cookie, true, this.dataset.dismissDays);
      }
    }

    customElements.define('pop-up', PopUp);
  });
}
